import * as React from 'react';
import { Box, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import MuiDataGrid from 'views/common/MuiDataGrid';
import {
    Toolbar,
    ToolbarButton,
    ExportCsv,
    ExportExcel,
    ExportPrint,
} from '@mui/x-data-grid-premium';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Preserved for future use (currently disabled below); intentionally unused
// eslint-disable-next-line no-unused-vars
const ExportMenu = () => {
    const [open, setOpen] = React.useState(false);
    const triggerRef = React.useRef(null);

    return (
        <React.Fragment>
            <Tooltip title="Export">
                <ToolbarButton
                    ref={triggerRef}
                    id="export-menu-trigger"
                    aria-controls="export-menu"
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={() => setOpen(true)}
                >
                    <FileDownloadIcon fontSize="small" />
                </ToolbarButton>
            </Tooltip>

            <Menu
                id="export-menu"
                anchorEl={triggerRef.current}
                open={open}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    list: {
                        'aria-labelledby': 'export-menu-trigger',
                    },
                }}
            >
                <ExportCsv
                    render={(props) => (
                        <MenuItem
                            {...props}
                            onClick={(event) => {
                                if (props.onClick) {
                                    props.onClick(event);
                                }
                                setOpen(false);
                            }}
                        >
                            Download as CSV
                        </MenuItem>
                    )}
                />
                <ExportExcel
                    render={(props) => (
                        <MenuItem
                            {...props}
                            onClick={(event) => {
                                if (props.onClick) {
                                    props.onClick(event);
                                }
                                setOpen(false);
                            }}
                        >
                            Download as Excel
                        </MenuItem>
                    )}
                />
                <ExportPrint
                    render={(props) => (
                        <MenuItem
                            {...props}
                            onClick={(event) => {
                                if (props.onClick) {
                                    props.onClick(event);
                                }
                                setOpen(false);
                            }}
                        >
                            Print
                        </MenuItem>
                    )}
                />
            </Menu>
        </React.Fragment>
    );
};

const CustomToolbar = () => {
    return (
        <Toolbar>
            <Typography sx={{
                flex: 1, mx: 0.5, fontWeight: "700",
                fontSize: "20px"
            }}>
                Outages
            </Typography>
            {/* <ExportMenu /> */}
        </Toolbar>
    );
};

// Helper function to format numbers with commas
const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString('en-US');
};


const columns = [
    {
        field: 'Year',
        headerName: 'Year',
        flex: 1,
        minWidth: 120,        
        renderCell: (params) => (
            <Typography sx={{ fontWeight: 600 }}>
                {params?.row?.Year}
            </Typography>
        ),
    },
    {
        field: 'Month',
        headerName: 'Month',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
            <Typography sx={{ fontWeight: 600 }}>
                {params?.row?.Month}
            </Typography>
        ),
    },
    // {
    //     field: 'Sectors',
    //     headerName: 'Sectors',
    //     flex: 1,
    //     minWidth: 100,
    //     renderCell: (params) => (
    //         <Typography sx={{ fontWeight: 400 }}>
    //             {formatNumber(params.value)}
    //         </Typography>
    //     ),
    // },
    {
        field: 'JMA_Minutes',
        headerName: 'JMA Outage Minutes',
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
            <Typography sx={{ fontWeight: 400 }}>
                {params.value}
            </Typography>
        ),
    },
    {
        field: 'SU_Minutes',
        headerName: 'SU Outage Minutes',
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
            <Typography sx={{ fontWeight: 400 }}>
                {params.value}
            </Typography>
        ),
    },
    {
        field: 'Amount_Owed',
        headerName: 'Amount Owed',
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
            <Typography sx={{ fontWeight: 400 }}>
                $ {formatNumber(params.value)}
            </Typography>
        ),
    },
];

const OutagesContent = ({ outageData }) => {
    const rows =
        outageData?.months?.map((row, index) => ({
            id: index + 1,
            isSummary: index >= (outageData?.months?.length - 2),
            ...row,
        })) ?? [];

    // compute totals over all rows
    // const totals = rows.reduce(
    //     (acc, row) => {
    //         acc.JMA_Seconds += Number(row.JMA_Seconds || 0);
    //         acc.SU_Seconds += Number(row.SU_Seconds || 0);
    //         acc.Amount_Owed += Number(row.Amount_Owed || 0);
    //         return acc;
    //     },
    //     { JMA_Seconds: 0, SU_Seconds: 0, Amount_Owed: 0 }
    // );

    // const pinnedRows = {
    //     bottom: [
    //         {
    //             id: 'total-row',
    //             Year: new Date().getFullYear(),          // leave empty if you want
    //             Month: 'Total',
    //             JMA_Seconds: totals.JMA_Seconds,
    //             SU_Seconds: totals.SU_Seconds,
    //             Amount_Owed: totals.Amount_Owed,
    //         },
    //     ],
    // };

    return (
        <Box sx={{ width: '100%', p: { xs: '0.5rem', sm: '1.3vw' } }}>
            <MuiDataGrid
                rows={rows}
                label="Outages"
                columns={columns}
                pagination={false}
                density="compact"
                datagridName="outages"
                isEdit={false}
                showToolbar={true}
                slots={{ toolbar: CustomToolbar }}
                getRowClassName={(params) => 
                    params.row.isSummary ? 'summary-row' : ''
                }
                sx={{
                    '& .summary-row': {
                        backgroundColor: '#FFF8E1 !important',
                        borderTop: '3px solid #E0D6B8',
                        fontWeight: '700 !important',
                        '&:hover': {
                            backgroundColor: '#FFF3CC !important',
                        },
                    },
                    '& .summary-row .MuiDataGrid-cell': {
                        fontWeight: '700 !important',
                    },
                }}
            />
        </Box>
    );
};

export default OutagesContent;
